import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { SERVER_URL } from "../../config";
import { StatusBadge, Field, inputClass, SearchableSelect } from "./Shared";
import {
    postForm,
    getActionTakenBy,
    formatDate,
    formatMoney,
    monthLabel,
    subscriptionMonthOptions,
    computeTotalAmount,
} from "./utils";

/* ------------------------------------------------------------------ */
/* Small "Update renewal" popup                                        */
/* ------------------------------------------------------------------ */
const RenewalUpdateModal = ({ open, row, onClose, onSaved, notify }) => {
    const [activationDate, setActivationDate] = useState("");
    const [expireDate, setExpireDate] = useState("");
    const [nextRenewalDate, setNextRenewalDate] = useState("");
    const [subscription, setSubscription] = useState("1");
    const [rate, setRate] = useState("");
    const [discount, setDiscount] = useState("0");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && row) {
            // Activation Date defaults to the *current* expire date, per spec.
            setActivationDate(row.expire_date || "");
            setExpireDate(row.expire_date || "");
            setNextRenewalDate(row.next_renewal_date || "");
            setSubscription(String(row.subscription_months || row.subscription || "1"));
            setRate(row.rate ?? "");
            setDiscount(row.discount ?? "0");
        }
    }, [open, row]);

    if (!open || !row) return null;

    const totalAmount = computeTotalAmount(rate, subscription, discount, row.user_count || 1);

    const submit = async (renewal_status) => {
        setSaving(true);
        try {
            const res = await postForm(`${SERVER_URL}/Cloud/serverUserUpdate.php`, {
                action: "renew",
                slNo: row.primary_slNo,
                renewal_status,
                activation_date: activationDate,
                expire_date: expireDate,
                next_renewal_date: nextRenewalDate,
                subscription,
                rate,
                discount,
                action_taken_by: getActionTakenBy(),
            });
            if (res.data.success) {
                notify(`Renewal marked as ${renewal_status}.`);
                onClose();
                onSaved();
            } else {
                notify(res.data.message || "Failed to update renewal.", "error");
            }
        } catch {
            notify("Could not update renewal. Please try again.", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-md rounded-xl bg-white border border-slate-200 p-5 max-h-[90vh] overflow-y-auto" style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.18)" }}>
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-bold text-slate-800">Update Renewal</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
                </div>
                <p className="text-xs text-slate-500 mb-4">
                    {row.company_name} — {row.server_name} ({row.server_port})
                    {row.user_count > 1 && <span className="ml-1 text-indigo-600">· {row.user_count} linked seats</span>}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Activation Date" hint="Defaults to the current expire date">
                        <input type="date" value={activationDate || ""} onChange={(e) => setActivationDate(e.target.value)} className={inputClass} />
                    </Field>
                    <Field label="Expire Date">
                        <input type="date" value={expireDate || ""} onChange={(e) => setExpireDate(e.target.value)} className={inputClass} />
                    </Field>
                    <Field label="Next Renewal Date">
                        <input type="date" value={nextRenewalDate || ""} onChange={(e) => setNextRenewalDate(e.target.value)} className={inputClass} />
                    </Field>
                    <Field label="Subscription">
                        <SearchableSelect
                            options={subscriptionMonthOptions}
                            value={subscription}
                            onChange={setSubscription}
                            placeholder="Search months…"
                        />
                    </Field>
                    <Field label="Rate">
                        <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className={inputClass} />
                    </Field>
                    <Field label="Discount">
                        <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className={inputClass} />
                    </Field>
                </div>

                <div className="mt-3 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-700">Total Amount</span>
                    <span className="text-sm font-extrabold text-blue-800">{formatMoney(totalAmount)}</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">rate × users ({row.user_count || 1}) × months ({subscriptionMonthOptions.find(o => o.value === String(subscription))?.label || subscription}) − discount</p>

                <div className="flex flex-wrap justify-end gap-2 mt-4">
                    <button disabled={saving} onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs text-slate-500 border border-slate-200">Cancel</button>
                    <button disabled={saving} onClick={() => submit("Pending")} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-700 border border-amber-200 hover:bg-amber-100 bg-amber-50">Save as Pending</button>
                    <button disabled={saving} onClick={() => submit("Rejected")} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-700 border border-rose-200 hover:bg-rose-100 bg-rose-50">Reject</button>
                    <button disabled={saving} onClick={() => submit("Done")} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ background: "linear-gradient(90deg, #059669, #10b981)" }}>
                        {saving ? "Saving…" : "Mark Done"}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Main renewal report modal                                           */
/* ------------------------------------------------------------------ */
const RenewalReportModal = ({ open, onClose, notify }) => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [month, setMonth] = useState("");
    const [search, setSearch] = useState("");
    const [updateRow, setUpdateRow] = useState(null);

    const getEmployeeId = () => {
        try {
            let userData = localStorage.getItem('employee');
            if (!userData) userData = localStorage.getItem('user');
            const user = JSON.parse(userData || '{}');
            return user.employee_id || '';
        } catch {
            return '';
        }
    };

    const fetchReport = (selectedMonth) => {
        setLoading(true);
        const employee_id = getEmployeeId();
        axios
            .get(`${SERVER_URL}/Cloud/renewalReport.php`, {
                params: { employee_id, month: selectedMonth || month || undefined, search: search || undefined },
            })
            .then((res) => {
                if (res.data.success) {
                    setReport(res.data);
                    if (!month) setMonth(res.data.month);
                } else {
                    notify(res.data.message || "Failed to load report.", "error");
                }
            })
            .catch((err) => {
                console.error("Error fetching renewal report:", err);
                notify("Could not load renewal report.", "error");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (open) fetchReport();
    }, [open]);

    // Re-fetch whenever the month changes (server does the filtering).
    useEffect(() => {
        if (open && month) fetchReport(month);
    }, [month]);

    // Debounce the global search a little before hitting the server.
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => fetchReport(month), 350);
        return () => clearTimeout(t);
    }, [search]);

    const monthOptions = useMemo(
        () => (report?.available_months || []).map((m) => ({ label: monthLabel(m), value: m })),
        [report]
    );

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="w-full max-w-6xl max-h-[92vh] rounded-xl bg-white border border-slate-200 flex flex-col" style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>

                {/* Sticky header */}
                <div className="flex items-center justify-between gap-3 flex-wrap px-6 pt-5 pb-3 border-b border-slate-100">
                    <div>
                        <h2 className="text-lg font-bold text-slate-800">Renewal Report</h2>
                        <p className="text-xs text-slate-400">{monthLabel(month) || "This month"}'s renewals, grouped by customer.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="w-44">
                            <SearchableSelect
                                options={monthOptions}
                                value={month}
                                onChange={setMonth}
                                placeholder="Search month…"
                            />
                        </div>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search company, contact, server…"
                            className="w-64 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs"
                        />
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none px-1">✕</button>
                    </div>
                </div>

                <div className="overflow-y-auto px-6 py-4 flex-1">
                    {/* Value + percentage cards — visible to everyone */}
                    {report?.stats && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                            <div className="rounded-lg border border-slate-200 px-3 py-2.5">
                                <div className="text-[10px] uppercase text-slate-400 font-semibold">Total Renewal</div>
                                <div className="text-lg font-extrabold text-slate-800">{formatMoney(report.stats.total_value)}</div>
                                <div className="text-[11px] text-slate-400">{report.stats.total} record(s)</div>
                            </div>
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                                <div className="text-[10px] uppercase text-amber-700 font-semibold">Pending Renewal</div>
                                <div className="text-lg font-extrabold text-amber-800">{formatMoney(report.stats.pending_value)}</div>
                                <div className="text-[11px] text-amber-600">{report.stats.pending} record(s)</div>
                            </div>
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                                <div className="text-[10px] uppercase text-emerald-700 font-semibold">Renewal Done ({report.stats.success_percent}%)</div>
                                <div className="text-lg font-extrabold text-emerald-800">{formatMoney(report.stats.done_value)}</div>
                                <div className="text-[11px] text-emerald-600">{report.stats.done} record(s)</div>
                            </div>
                            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
                                <div className="text-[10px] uppercase text-rose-700 font-semibold">Rejected ({report.stats.lost_percent}% lost)</div>
                                <div className="text-lg font-extrabold text-rose-800">{formatMoney(report.stats.rejected_value)}</div>
                                <div className="text-[11px] text-rose-600">{report.stats.rejected} record(s)</div>
                            </div>
                        </div>
                    )}

                    {loading && <p className="text-sm text-slate-400 text-center py-6">Loading renewal report…</p>}

                    {!loading && report && (
                        <div className="overflow-x-auto rounded-lg border border-slate-100">
                            <table className="min-w-[1300px] w-full text-sm">
                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        <th className="p-2.5 text-left text-xs text-slate-500 font-semibold">Company</th>
                                        <th className="p-2.5 text-left text-xs text-slate-500 font-semibold">Contact Person</th>
                                        <th className="p-2.5 text-left text-xs text-slate-500 font-semibold">Contact Number</th>
                                        <th className="p-2.5 text-left text-xs text-slate-500 font-semibold">Server (Port)</th>
                                        <th className="p-2.5 text-center text-xs text-slate-500 font-semibold">Rate</th>
                                        <th className="p-2.5 text-center text-xs text-slate-500 font-semibold">Users</th>
                                        <th className="p-2.5 text-center text-xs text-slate-500 font-semibold">Subscription</th>
                                        <th className="p-2.5 text-center text-xs text-slate-500 font-semibold">Total Amount</th>
                                        <th className="p-2.5 text-center text-xs text-slate-500 font-semibold">Activation</th>
                                        <th className="p-2.5 text-center text-xs text-slate-500 font-semibold">Expire</th>
                                        <th className="p-2.5 text-center text-xs text-slate-500 font-semibold">Next Renewal</th>
                                        <th className="p-2.5 text-center text-xs text-slate-500 font-semibold">Status</th>
                                        <th className="p-2.5 text-center text-xs text-slate-500 font-semibold">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.data.length === 0 ? (
                                        <tr><td colSpan={13} className="p-8 text-center text-slate-400 text-sm">No renewals found for this month.</td></tr>
                                    ) : (
                                        report.data.map((row) => (
                                            <tr key={row.primary_slNo} className="border-t border-slate-100 hover:bg-blue-50/50">
                                                <td className="p-2.5 text-xs text-slate-800 font-medium">{row.company_name}</td>
                                                <td className="p-2.5 text-xs text-slate-600">{row.contact_person_name || "—"}</td>
                                                <td className="p-2.5 text-xs text-slate-600">{row.contact_person_number || "—"}</td>
                                                <td className="p-2.5 text-xs text-slate-600">{row.server_name} ({row.server_port})</td>
                                                <td className="p-2.5 text-xs text-center text-slate-600">{formatMoney(row.rate)}</td>
                                                <td className="p-2.5 text-xs text-center font-semibold text-indigo-600">{row.user_count}</td>
                                                <td className="p-2.5 text-xs text-center text-slate-600">{row.subscription_months} mo</td>
                                                <td className="p-2.5 text-xs text-center font-bold text-slate-800">{formatMoney(row.total_amount)}</td>
                                                <td className="p-2.5 text-xs text-center text-slate-500">{formatDate(row.activation_date)}</td>
                                                <td className="p-2.5 text-xs text-center text-slate-500">{formatDate(row.expire_date)}</td>
                                                <td className="p-2.5 text-xs text-center text-slate-500">{formatDate(row.next_renewal_date)}</td>
                                                <td className="p-2.5 text-center"><StatusBadge status={row.renewal_status} /></td>
                                                <td className="p-2.5 text-center">
                                                    <button onClick={() => setUpdateRow(row)} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-100 bg-blue-50">
                                                        Update
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <RenewalUpdateModal
                open={!!updateRow}
                row={updateRow}
                onClose={() => setUpdateRow(null)}
                onSaved={() => fetchReport(month)}
                notify={notify}
            />
        </div>
    );
};

export default RenewalReportModal;
